import React from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { lightPalette, palette } from '@app/theme';
import { tableHeaderCellSx, zebraRowSx } from '@components/ui/AnalysisReport/analysisReport.utilities';
import { ScrollableTable } from '@components/ui/ScrollableTable';

import type { MarkdownContentProperties, MarkdownContentVariant } from './MarkdownContent.types';
import type { Components } from 'react-markdown';


// ─── Variant styles ───────────────────────────────────────────────

type VariantConfig = {
  fontSize: number;
  lineHeight: number;
  color: string;
  showHeadings: boolean;
  paragraphGap: number;
};

const VARIANTS: Record<MarkdownContentVariant, VariantConfig> = {
  section: {
    fontSize: 15,
    lineHeight: 1.7,
    color: lightPalette.text.secondary,
    showHeadings: true,
    paragraphGap: 1.5,
  },
  thesis: {
    fontSize: 15,
    lineHeight: 1.7,
    color: lightPalette.text.primary,
    showHeadings: false,
    paragraphGap: 1.5,
  },
  rationale: {
    fontSize: 15,
    lineHeight: 1.7,
    color: 'inherit',
    showHeadings: false,
    paragraphGap: 1,
  },
  compact: {
    fontSize: 13,
    lineHeight: 1.55,
    color: 'inherit',
    showHeadings: false,
    paragraphGap: 1,
  },
};

// ─── MUI component overrides for react-markdown ──────────────────

function buildComponents(config: VariantConfig): Components {
  const { fontSize, lineHeight, paragraphGap, showHeadings } = config;

  const listSx = {
    pl: 2.5,
    mb: paragraphGap,
    fontSize,
    lineHeight,
    '&:last-child': { mb: 0 },
    // GFM task list: маркер списка заменяется чекбоксом
    '& .task-list-item': { listStyle: 'none', ml: -2.5 },
    '& .task-list-item input': { mr: 1, verticalAlign: 'middle' },
  };

  return {
    p: ({ children }) => (
      <Typography
        component='p'
        sx={{ fontSize, lineHeight, m: 0, mb: paragraphGap, '&:last-child': { mb: 0 } }}
      >
        {children}
      </Typography>
    ),

    strong: ({ children }) => (
      <Typography component='span' sx={{ fontWeight: 600, color: lightPalette.text.primary }}>
        {children}
      </Typography>
    ),

    em: ({ children }) => (
      <Typography component='span' sx={{ fontStyle: 'italic', color: lightPalette.text.muted }}>
        {children}
      </Typography>
    ),

    del: ({ children }) => (
      <Typography component='del' sx={{ textDecoration: 'line-through', color: lightPalette.text.muted }}>
        {children}
      </Typography>
    ),

    h3: showHeadings
      ? ({ children }) => (
        <Typography
          variant='subtitle1'
          component='h3'
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: lightPalette.text.primary,
            mt: 3,
            mb: 1,
            '&:first-of-type': { mt: 0 },
          }}
        >
          {children}
        </Typography>
      )
      : ({ children }) => (
        <Typography component='p' sx={{ fontSize, lineHeight, fontWeight: 600, color: lightPalette.text.primary, m: 0, mb: paragraphGap }}>
          {children}
        </Typography>
      ),

    // GFM-сноски дают якорные ссылки внутри страницы (`#user-content-fn-1`). Открывать их
    // в новой вкладке нельзя — переход просто не сработает, поэтому `target`/`rel` ставим
    // только внешним ссылкам. `id` пробрасываем: без него сноска ведёт в никуда.
    a: ({ href, children, id }) => (
      <Link
        href={href}
        id={id}
        {...(href?.startsWith('#') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        sx={{ color: palette.primary.main }}
      >
        {children}
      </Link>
    ),

    ul: ({ children, className }) => (
      <Box component='ul' className={className} sx={listSx}>
        {children}
      </Box>
    ),

    // Task list бывает и нумерованным (`1. [x] ...`) — стили те же, иначе рядом с чекбоксом
    // остаётся номер списка.
    ol: ({ children, className }) => (
      <Box component='ol' className={className} sx={listSx}>
        {children}
      </Box>
    ),

    // `id` нужен пунктам списка сносок — на них указывают ссылки из текста.
    li: ({ children, className, id }) => (
      <Typography component='li' className={className} id={id} sx={{ fontSize, lineHeight, mb: 0.5 }}>
        {children}
      </Typography>
    ),

    // ─── GFM tables ───────────────────────────────────────────────

    // Широкие таблицы (сценарии выручки по годам) не должны ломать вёрстку карточки:
    // скроллом и подсказкой о скрытых колонках заведует ScrollableTable
    table: ({ children }) => (
      <ScrollableTable dataTest='markdown-table' sx={{ mb: paragraphGap, '&:last-child': { mb: 0 } }}>
        <Table size='small'>{children}</Table>
      </ScrollableTable>
    ),

    thead: ({ children }) => <TableHead>{children}</TableHead>,

    tbody: ({ children }) => <TableBody>{children}</TableBody>,

    tr: ({ children }) => <TableRow sx={zebraRowSx}>{children}</TableRow>,

    // Выравнивание колонок (`:---`, `:---:`, `---:`) приходит из hast-util-to-jsx-runtime
    // готовым инлайновым `style={{ textAlign }}` — пробрасываем его как есть, чтобы оно
    // перебило дефолтный `text-align: left` у MuiTableCell. Без alignment-разметки
    // `style` не приходит вовсе и остаётся дефолт MUI.
    th: ({ children, style }) => (
      <TableCell style={style} sx={tableHeaderCellSx}>
        {children}
      </TableCell>
    ),

    td: ({ children, style }) => (
      <TableCell
        style={style}
        sx={{
          fontSize: 13,
          color: lightPalette.text.secondary,
          borderBottom: `1px solid ${lightPalette.border.light}`,
          py: 1.5,
          px: 2,
        }}
      >
        {children}
      </TableCell>
    ),
  };
}

// ─── Component ───────────────────────────────────────────────────

export const MarkdownContent: React.FC<MarkdownContentProperties> = ({
  children,
  variant = 'section',
  sx,
  dataTest,
}) => {
  const config = VARIANTS[variant];
  const components = React.useMemo(() => buildComponents(config), [variant]);

  // Отчёт рендерит несколько MarkdownContent на одной странице, и у каждого сноски
  // нумеруются с единицы. С дефолтным префиксом `user-content-` их id совпадают,
  // и ссылка на сноску из второго раздела уводит в первый. Двоеточия из useId убираем:
  // они валидны в id, но ломают querySelector в тестах и потребительском коде.
  const instanceId = React.useId().replaceAll(':', '');
  const clobberPrefix = `${instanceId}-`;

  return (
    <Box
      data-testid={dataTest}
      sx={[
        {
          fontSize: config.fontSize,
          lineHeight: config.lineHeight,
          color: config.color,
        },
        // Заголовок блока сносок генерируется с классом `sr-only`, но своего CSS для этого
        // класса в проекте нет — без правила ниже он рендерится как обычный видимый h2.
        {
          '& .sr-only': {
            position: 'absolute',
            // Именно строки: числовой `width: 1` в sx MUI трактует как 100%
            // (sizingTransform: value <= 1 → проценты), а не как один пиксель.
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap',
            border: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/*
        singleTilde выключен намеренно: в финансовом тексте одиночная тильда — это «примерно»
        (`~17.5`), и диапазон вида `~17.5~20` иначе превращается в зачёркнутое `17.5`.
        Явное `~~зачёркнуто~~` продолжает работать.
        Подписи блока сносок локализованы — по умолчанию они английские.
      */}
      <Markdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
        remarkRehypeOptions={{
          footnoteLabel: 'Сноски',
          footnoteBackLabel: 'Вернуться к тексту',
          clobberPrefix,
        }}
        components={components}
      >
        {children}
      </Markdown>
    </Box>
  );
};
